import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Check, Database } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ModuleTitleCard, ContainerCard } from "../../components/ui/cards";
import {
    EMPLOYEE_DEPARTMENT,
    EMPLOYEE_DESIGNATION,
    EMPLOYEE_TYPE,
    EMPLOYEE_GROUP,
    EMPLOYEE_SHIFT,
    USERGROUP,
    EMPLOYEE_MIGRATION_PROCESS,
} from "../../constants/api";

import { ensureKeys } from "./migrationConfig";
import { migrationBranchFilter } from "./migrationBranch";
import UploadStep from "./steps/UploadStep";
import ReviewStep from "./steps/ReviewStep";
import MigrateStep from "./steps/MigrateStep";
import ReportingManagerStep from "./steps/ReportingManagerStep";

const STEPS = ["Upload", "Review & Fix", "Migrate", "Assign Managers"];

// 0 Upload · 1 Review · 2 Migrate · 3 Reporting managers
const Stepper = ({ current }) => (
    <div className="flex items-center gap-2 px-3 pb-2 overflow-x-auto">
        {STEPS.map((label, i) => {
            const done = i < current;
            const active = i === current;
            return (
                <div key={label} className="flex items-center gap-2 shrink-0">
                    <div
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold border
                            ${done ? "bg-green-600 border-green-600 text-white"
                                : active ? "bg-sidebar-primary border-sidebar-primary text-white"
                                    : "bg-transparent border-gray-300 text-gray-400 dark:border-gray-600"}`}
                    >
                        {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <span className={`text-xs font-medium whitespace-nowrap ${active ? "text-gray-900 dark:text-white" : "text-muted-foreground"}`}>
                        {label}
                    </span>
                    {i < STEPS.length - 1 && (
                        <span className={`w-8 h-px ${done ? "bg-green-600" : "bg-gray-300 dark:bg-gray-600"}`} />
                    )}
                </div>
            );
        })}
    </div>
);

const MigrationWizard = () => {
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const location = useLocation();
    const { fetch } = useApiMethods();

    const [step, setStep] = useState(0);
    const [rows, setRows] = useState([]);
    const [migrationId, setMigrationId] = useState(routeId || null);
    const [counts, setCounts] = useState({ processed: 0, conflict: 0 });
    const [options, setOptions] = useState({
        department: [], designation: [], emptype: [], shift: [], usergroup: [], empgroup: [],
    });
    const [booting, setBooting] = useState(!!routeId);

    const backToList = () => navigate("/datamigration/employeemigration");
    const processExtra = useMemo(() => migrationBranchFilter(), []);

    // --- load master dropdowns once (shared by the grid + RM step) ----------
    useEffect(() => {
        let cancelled = false;
        const drop = { drop_down: "True" };
        const load = async () => {
            try {
                const [department, designation, emptype, empgroup, shift, usergroup] = await Promise.all([
                    fetch(EMPLOYEE_DEPARTMENT, drop),
                    fetch(EMPLOYEE_DESIGNATION, drop),
                    fetch(EMPLOYEE_TYPE, drop),
                    fetch(EMPLOYEE_GROUP, drop),
                    fetch(EMPLOYEE_SHIFT, drop),
                    fetch(USERGROUP, drop),
                ]);
                if (cancelled) return;
                const pick = (r) => (r?.status === "success" && Array.isArray(r.data) ? r.data : []);
                setOptions({
                    department: pick(department),
                    designation: pick(designation),
                    emptype: pick(emptype),
                    empgroup: pick(empgroup),
                    shift: pick(shift),
                    usergroup: pick(usergroup),
                });
            } catch {
                /* selects fall back to "no options"; cells just flag invalid */
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- resume an existing batch from the URL (refresh-safe) ---------------
    const bootedRef = useRef(false);
    useEffect(() => {
        if (!routeId || bootedRef.current) return;
        bootedRef.current = true;
        const resumeStatus = location.state?.status;
        if (resumeStatus === "completed") {
            setStep(3);
            setBooting(false);
            return;
        }
        const load = async () => {
            try {
                const res = await fetch(EMPLOYEE_MIGRATION_PROCESS, {
                    migration_id: routeId,
                    ...migrationBranchFilter(),
                });
                if (res?.status === "success" && res.data) {
                    setRows(ensureKeys(res.data.employee_data || []));
                    setCounts({
                        processed: res.data.processed_records || 0,
                        conflict: res.data.conflict_records || 0,
                    });
                }
                setStep(1);
            } catch {
                ToastMsg("error", "Could not load this migration batch");
                backToList();
            } finally {
                setBooting(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeId]);

    const handleParsed = useCallback((parsedRows) => {
        setRows(parsedRows);
        setStep(1);
    }, []);

    const handleProcessed = useCallback((id, nextCounts, conflictRows) => {
        if (id) setMigrationId(id);
        setCounts(nextCounts);
        setRows(ensureKeys(conflictRows));
    }, []);

    const handleMigrated = useCallback(() => setStep(3), []);

    if (booting) {
        return (
            <div>
                <ModuleTitleCard Icon={Database} title="Employee Migration" />
                <ContainerCard>
                    <div className="h-40 animate-pulse bg-gray-100 dark:bg-gray-800 rounded-lg m-4" />
                </ContainerCard>
            </div>
        );
    }

    return (
        <ContainerCard>
            <ModuleTitleCard Icon={Database} title="Employee Migration" />

            <>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="pt-4">
                        <Stepper current={step} />
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700" />

                    {step === 0 && (
                        <UploadStep options={options} onParsed={handleParsed} onCancel={backToList} />
                    )}
                    {step === 1 && (
                        <ReviewStep
                            rows={rows}
                            setRows={setRows}
                            options={options}
                            migrationId={migrationId}
                            counts={counts}
                            onProcessed={handleProcessed}
                            onNext={() => setStep(2)}
                        onCancel={backToList}
                        processExtra={processExtra}
                    />
                )}
                {step === 2 && (
                    <MigrateStep
                        migrationId={migrationId}
                        counts={counts}
                        onMigrated={handleMigrated}
                        onCancel={backToList}
                        processExtra={processExtra}
                    />
                    )}
                    {step === 3 && (
                        <ReportingManagerStep
                            migrationId={migrationId}
                            options={options}
                            onDone={backToList}
                        />
                    )}
                </div>
            </>
        </ContainerCard>
    );
};

export default MigrationWizard;
